import { Modal, Box, Button, Backdrop } from "@mui/material";
import { useState, useEffect } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { postClassIdsAndRoles } from "../../../../components/Api/Post";
import { useMutation } from "react-query";
import CircularProgress from "@mui/material/CircularProgress";
import isAuthenticated from "../../../../components/Utils/isAuthenticated";
import InstructorCell from "./InstructorCell";
import { selectedClassAtom, instanceRowsAtom } from "../../../../components/Atoms/atoms";
import { useAtom, useAtomValue } from "jotai";
import FacultyRankAutocomplete from "../../../../components/Utils/FacultyRankAutocomplete";
import ListSelect from "../../../../components/Utils/ListSelect";
import {
	useFacultyRank,
	useAddInstructorToFacultyRank,
} from "../../../../Hooks/useFacultyRankQuery";

const style = {
	position: "absolute",
	top: "50%",
	left: "50%",
	transform: "translate(-50%, -50%)",
	width: "90vw",
	height: "80svh",
	bgcolor: "background.paper",
	border: "2px solid #275d38",
	borderRadius: 2,
	display: "flex",
	flexDirection: "column",
};

const prependValues = [1, 2, 3, 4, 5, 6, 7, 8, 9];

function getFirstDigit(classId) {
	classId = classId.toString();
	const classId0 = classId[0];
	return Number(classId0);
}

function getLast4DigitsOfClassId(classId) {
	classId = classId.toString();
	const classId1 = classId[1];
	const classId2 = classId[2];
	const classId3 = classId[3];
	const classId4 = classId[4];
	return Number(classId1 + classId2 + classId3 + classId4);
}
//console.log( classAdjustRows);
function getNextAvailableInstanceId(classid, existingClassIds) {
	const listOfBadDigits = [];
	const firstDigit = getFirstDigit(classid);
	listOfBadDigits.push(firstDigit);
	existingClassIds?.forEach((existingClassId) => {
		const last4DigitsExisting = getLast4DigitsOfClassId(existingClassId);
		const last4DigitsCurrent = getLast4DigitsOfClassId(classid);
		if (last4DigitsExisting === last4DigitsCurrent) {
			const firstDigit = getFirstDigit(existingClassId);
			listOfBadDigits.push(firstDigit);
		}
	});
	listOfBadDigits.sort((a, b) => a - b);
	const diff = prependValues.filter((x) => !listOfBadDigits.includes(x));
	const last4Digits = getLast4DigitsOfClassId(classid);
	const nextAvailableId = diff[0] * 10000 + last4Digits;
	return nextAvailableId;
}

export default function InstancedClassAdjustModal(props) {
	const { classAdjustRows, deptId, handleRefresh, open, setOpen } = props;

	const auth = isAuthenticated();
	const token = auth.token;
	const selectedClass = useAtomValue(selectedClassAtom);

	const classRoles = ["PI", "TA", "CC", "CO", "LA", "GL", "AI", "PG", "SG", "TC", "DA"];
	const rolesDescriptions = {
		PI: "Primary Instructor",
		TA: "Teaching Assistant",
		CC: "Course Coordinator",
		CO: "Co-Instructor",
		LA: "Lab Assistant",
		GL: "Grader/Lab Assistant",
		AI: "Assistant Instructor",
		PG: "Primary Grader",
		SG: "Secondary Grader",
		TC: "Teaching Coordinator",
		DA: "Department Assistant",
	};
	const [instructorRole, setInstructorRole] = useState("");

	const [instanceRows, setInstanceRows] = useAtom(instanceRowsAtom);

	const [openInstructorDialog, setOpenInstructorDialog] = useState(false);
	const [classForInstance, setClassForInstance] = useState({});
	const [newRoleValue, setNewRoleValue] = useState("");
	const [listOfAddedClasses, setListOfAddedClasses] = useState([]);

	const { data: facultyRank } = useFacultyRank(token);

	const { addInstructorToFacultyRank } = useAddInstructorToFacultyRank(token, deptId);

	const [newlyAddedToFacultyRank, setNewlyAddedToFacultyRank] = useState(false);

	const [hasAddedInstructor, setHasAddedInstructor] = useState(false);

	useEffect(() => {
		if (selectedClass) {
			let listOfClasses = [];
			listOfClasses.push(selectedClass);
			if (classAdjustRows && classAdjustRows.length > 0) {
				classAdjustRows.forEach((row) => {
					if (row.original === selectedClass.classid) {
						listOfClasses.push(row);
					} else if (selectedClass.original && row.classid === selectedClass.original) {
						listOfClasses.push(row);
					}
				});
			}
			setInstanceRows(listOfClasses);
		}
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedClass, classAdjustRows]);

	// console.log(classAdjustRows);

	function getNewRows() {
		let listOfNewRows = [];
		instanceRows.forEach((row) => {
			if (row.new) {
				listOfNewRows.push(row);
			}
		});
		return listOfNewRows;
	}

	const checkExistingFacultyRank = (empid) => {
		let exists = false;
		facultyRank.forEach((rank) => {
			if (Number(rank.empid) === Number(empid)) {
				exists = true;
			}
		});
		return exists;
	};

	const addInstructor = async (instructorToAdd) => {
		const success = await addInstructorToFacultyRank(instructorToAdd);
		return success;
	};

	const handleAddInstructor = async () => {
		const instructorToAdd = newRoleValue;

		let newRole = {};

		const updatedRows = instanceRows.map((classObj) => {
			if (classObj.classid === classForInstance.classid) {
				newRole = {
					ccid: instructorToAdd.ccid,
					class_role: instructorRole,

					checked: false,
					classid: classObj.classid,
					disabled: false,
					empid: instructorToAdd.empid,
					name: instructorToAdd.name,
					first_name: instructorToAdd.name.split(", ")[0],
					last_name: instructorToAdd.name.split(", ")[1],
					role_description: rolesDescriptions[instructorRole],
					roleid: Number(instructorToAdd.empid) + classObj.classid,
					term: classObj.term,
					import_status: "MANUAL",
				};

				if (classObj.teamsize === 0 || (classObj.teamsize !== 0 && classObj.roles.length === 0)) {
					return {
						...classObj,
						name: instructorToAdd.name,
						empid: instructorToAdd.empid,

						class_role: instructorRole,
						teamsize: 1,
						comb_classes: [],
						roles: [newRole],
					};
				} else {
					const updatedRoles = [...classObj.roles, newRole];
					const teamNames = updatedRoles.map((role) => `'${role.name}'`).join(", ");
					return {
						...classObj,
						roles: updatedRoles,
						teamName: teamNames,
						teamsize: classObj.teamsize + 1,
					};
				}
			}
			return classObj;
		});

		setInstanceRows([...updatedRows]);

		setInstructorRole("");
		setHasAddedInstructor(true);
		setNewRoleValue("");
	};

	const handleChangeRole = (event) => {
		setInstructorRole(event.target.value);
	};

	const handleAddInstancedClassSame = () => {
		const classid = selectedClass.original ? selectedClass.original : selectedClass.classid;
		const newClassidInt = getNextAvailableInstanceId(
			selectedClass.classid,
			instanceRows?.map((c) => c.classid),
		);
		const newClass = {
			...selectedClass,
			classid: newClassidInt,
			original: classid,
			new: true,
		};

		setInstanceRows([...instanceRows, newClass]);
		setListOfAddedClasses([...listOfAddedClasses, newClassidInt]);
	};

	const handleAddInstancedClassDifferent = () => {
		const classid = selectedClass.original ? selectedClass.original : selectedClass.classid;
		const newClassidInt = getNextAvailableInstanceId(
			selectedClass.classid,
			instanceRows.map((c) => c.classid),
		);

		const newClass = {
			...selectedClass,
			classid: newClassidInt,
			original: classid,
			teamsize: 0,
			roles: [],
			new: true,
			needsInstructor: true,
		};

		const oldRows = [...instanceRows];

		setInstanceRows([...oldRows, newClass]);

		setClassForInstance(newClass);
		setListOfAddedClasses([...listOfAddedClasses, newClassidInt]);
		setOpenInstructorDialog(true);
		setHasAddedInstructor(false);
	};

	const handleCancelAddInstructor = () => {
		if (classForInstance?.roles?.length === 0 || !classForInstance?.roles) {
			//delete the row
			const updatedRows = instanceRows.filter((row) => row.classid !== classForInstance.classid);
			setInstanceRows(updatedRows);
		}
		setNewRoleValue("");
		setNewlyAddedToFacultyRank(null);
		setOpenInstructorDialog(false);
		setInstructorRole("");
		setHasAddedInstructor(false);
	};

	const postClassIdsRolesMutation = useMutation(({ token, listOfNewRows }) =>
		postClassIdsAndRoles({ token, listOfNewRows }),
	);

	const handleSubmit = async () => {
		const listOfNewRows = getNewRows(); // NOPE

		postClassIdsRolesMutation.mutateAsync(
			{ token, listOfNewRows },
			{
				onSuccess: (data, error) => {
					setListOfAddedClasses([]);
					handleRefresh();
					setOpen(false);
				},
			},
		);

		setOpen(false);
	};

	const handleClose = (event, reason) => {
		if (reason && reason === "backdropClick") {
			return;
		} else {
			setOpen(false);
		}
	};

	const handleDelete = (params) => {
		const updatedRows = [...instanceRows];
		const index = updatedRows.findIndex((r) => r.classid === params.row.classid);
		updatedRows.splice(index, 1);
		setInstanceRows(updatedRows);
	};

	const renderDeleteButton = (params) => {
		if (params.row.new) {
			return (
				<div>
					<Button
						variant="contained"
						color="error"
						size="small"
						onClick={() => handleDelete(params)}
					>
						Delete
					</Button>
				</div>
			);
		} else {
			return <div></div>;
		}
	};

	const renderInstructorName = (params) => {
		return <InstructorCell row={params.row} />;
	};

	const columns = [
		{
			field: "classid",
			headerName: "Class ID",
			width: 100,
		},
		{
			field: "ctitle",
			headerName: "Class Title",
			width: 180,
		},

		{
			field: "csize",
			headerName: "Size",
			width: 60,
		},
		{
			field: "name",
			headerName: "Instructor Name",
			width: 180,
			flex: 1,
			renderCell: renderInstructorName,
		},
		{
			field: "c_edate",
			headerName: "Class End Date",
			width: 110,
			disableClickEventBubbling: true,
		},

		{
			field: "examdate",
			headerName: "Exam Date",
			width: 110,
			disableClickEventBubbling: true,
		},

		{
			field: "c_location",
			headerName: "Location",
			width: 80,
			disableClickEventBubbling: true,
		},
		{
			field: "teamsize",
			headerName: "Team Size",
			width: 80,
			disableClickEventBubbling: true,
		},
		{
			field: "delete",
			headerName: "",
			width: 80,
			renderCell: renderDeleteButton,
		},
	];

	return (
		<div>
			<Modal
				open={open}
				onClose={handleClose}
				aria-labelledby="modal-modal-title"
				aria-describedby="modal-modal-description"
			>
				<Box sx={style}>
					{/* Loading overlay for saving */}
					<Backdrop
						open={postClassIdsRolesMutation.isLoading}
						sx={{
							color: "#fff",
							zIndex: (theme) => theme.zIndex.modal + 1,
							position: "absolute",
							borderRadius: 2,
						}}
					>
						<div className="flex flex-col items-center gap-3">
							<CircularProgress color="inherit" />
							<span className="text-white font-medium">Saving instance...</span>
						</div>
					</Backdrop>

					<Box
						sx={{
							height: "100%",
							width: "100%",
							p: 2,
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
							flexDirection: "column",
						}}
					>
						<Box sx={{ display: "flex", width: "100%", height: "80%" }}>
							<DataGrid
								sx={{
									"&.MuiDataGrid-root .MuiDataGrid-cell:focus-within": {
										outline: "None !important",
									},
									"& .MuiDataGrid-virtualScroller::-webkit-scrollbar": {
										width: "0.4em",
									},
									"& .MuiDataGrid-virtualScroller::-webkit-scrollbar-track": {
										background: "#f1f1f1",
									},
									"& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb": {
										backgroundColor: "#888",
									},
									"& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb:hover": {
										background: "#555",
									},

									borderRadius: "1px 1px 4px 4px",
								}}
								disableSelectionOnClick
								onRowClick={(params) => {}}
								rows={instanceRows}
								density="compact"
								columns={columns}
								initialState={{
									pagination: {
										paginationModel: { pageSize: 100, page: 0 },
									},
								}}
								pageSizeOptions={[5, 10]}
								getRowId={(row) => row.classid}
							/>
						</Box>
						<div className="pt-4">
							{instanceRows && instanceRows.length === 9 ? (
								<Button variant="contained" color="error" sx={{ marginLeft: "10px" }}>
									Max Instances Reached
								</Button>
							) : openInstructorDialog ? (
								<div className="min-w-[500px] p-4 border-2 rounded-md shadow-md flex flex-col w-full gap-2">
									<p className="text-center max-w-[600px]">
										Please search for and add instructors. You can add multiple to the same
										instanced class. Press "Done" when you have finished adding instructors for each
										instance.
									</p>
									<div>
										{newlyAddedToFacultyRank ? (
											<div className="flex  w-full items-center gap-2 py-2">
												<div className=" rounded-md p-4">Currently Adding:</div>
												<div className="border-2 border-gray-300 text-primary font-semibold rounded-md p-4">
													{newlyAddedToFacultyRank.name} - {newlyAddedToFacultyRank.empid}
												</div>
												<ListSelect
													required
													marginBottom="0"
													label="Role"
													error={newlyAddedToFacultyRank && !instructorRole}
													value={instructorRole}
													onChange={handleChangeRole}
													list={classRoles}
													width={100}
												/>
											</div>
										) : (
											<div className="flex  w-full items-center gap-2 py-2">
												<FacultyRankAutocomplete
													options={facultyRank}
													currentRow={selectedClass}
													isInInstance
													newRoleValue={newRoleValue}
													setNewRoleValue={setNewRoleValue}
													setNewlyAddedToFacultyRank={setNewlyAddedToFacultyRank}
													setExistingRoles={() => {}}
													checkExistingFacultyRank={checkExistingFacultyRank}
													addInstructorToFacultyRank={addInstructor}
												/>
												<ListSelect
													required
													marginBottom="0"
													label="Role"
													error={newRoleValue && !instructorRole}
													value={instructorRole}
													onChange={handleChangeRole}
													list={classRoles}
													width={100}
												/>
											</div>
										)}

										<div className="flex items-center w-full gap-2">
											<Button
												sx={{ width: "100%" }}
												color="error"
												variant="contained"
												onClick={handleCancelAddInstructor}
											>
												Cancel
											</Button>

											<Button
												variant="outlined"
												disabled={!hasAddedInstructor}
												sx={{ width: "100%" }}
												onClick={() => {
													setOpenInstructorDialog(false);
												}}
											>
												Done
											</Button>

											<Button
												variant="contained"
												disabled={
													// if no instructor role, and if either no newRoleValue or newlyAddedToFacultyRank
													!instructorRole || (!newRoleValue && !newlyAddedToFacultyRank)
												}
												onClick={handleAddInstructor}
												sx={{ width: "100%" }}
											>
												Add to Course
											</Button>
										</div>
									</div>
								</div>
							) : (
								<div className="py-4">
									<Button
										disabled={instanceRows.every((row) => row.roles && row.roles.length === 0)}
										variant="contained"
										onClick={handleAddInstancedClassSame}
										sx={{ marginLeft: "10px" }}
									>
										Add Instanced Class With Same Instructor(s)
									</Button>
									<Button
										variant="outlined"
										onClick={handleAddInstancedClassDifferent}
										sx={{ marginLeft: "10px" }}
									>
										Add Instanced Class With Different Instructor(s)
									</Button>
								</div>
							)}
						</div>
						<Box
							sx={{
								width: "100%",
								height: "20%",
								display: "flex",
								flexDirection: "row",
								justifyContent: "end",
								alignItems: "end",
								gap: "10px",
							}}
						>
							<Button
								variant="contained"
								color="error"
								disabled={postClassIdsRolesMutation.isLoading}
								sx={{
									marginTop: "5px",
									width: "100px",
									display: "flex",
								}}
								onClick={handleClose}
							>
								Cancel
							</Button>
							{postClassIdsRolesMutation.isLoading ? (
								<Button
									variant="contained"
									sx={{
										marginTop: "5px",
										width: "100px",
										display: "flex",
									}}
								>
									<CircularProgress size={20} />
								</Button>
							) : (
								<Button
									variant="contained"
									disabled={
										getNewRows().length === 0 ||
										postClassIdsRolesMutation.isLoading ||
										openInstructorDialog ||
										listOfAddedClasses.length === 0
									}
									sx={{
										marginTop: "5px",
										width: "100px",
										display: "flex",
									}}
									onClick={handleSubmit}
								>
									Confirm
								</Button>
							)}
						</Box>
					</Box>
				</Box>
			</Modal>

			{/*Add instructor to the role, not push right away.*/}
		</div>
	);
}
